import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  styled,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';

// Custom theme with teal/light green palette for trust
const theme = createTheme({
  palette: {
    primary: {
      main: '#009688', // Teal
      light: '#e0f2f1',
      dark: '#00796b',
    },
    secondary: {
      main: '#4caf50', // Light Green
      light: '#e8f5e9',
      dark: '#388e3c',
    },
    success: {
      main: '#66bb6a',
      light: '#e8f5e9',
    },
    background: {
      default: '#fafafa',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 16,
  },
});

// Custom styled components
const StyledCard = styled(Card)(({ theme, isRecommended }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  transform: isRecommended ? 'scale(1.05)' : 'none',
  border: isRecommended ? `2px solid ${theme.palette.primary.main}` : '1px solid rgba(0, 0, 0, 0.08)',
  borderRadius: 20,
  overflow: 'visible', // Important for the chip to overflow
  backgroundColor: isRecommended ? 'rgba(224, 242, 241, 0.5)' : '#fff',
  backgroundImage: isRecommended 
    ? 'linear-gradient(135deg, rgba(224, 242, 241, 0.8) 0%, rgba(255, 255, 255, 1) 100%)'
    : 'linear-gradient(135deg, rgba(250, 250, 250, 0.8) 0%, rgba(255, 255, 255, 1) 100%)',
  boxShadow: isRecommended 
    ? '0 10px 28px rgba(0, 150, 136, 0.15)'
    : '0 6px 16px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
  '&:hover': {
    transform: isRecommended ? 'scale(1.07)' : 'scale(1.03)',
    boxShadow: isRecommended 
      ? '0 15px 35px rgba(0, 150, 136, 0.2)'
      : '0 12px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const StyledCardHeader = styled(CardHeader)(({ theme, isRecommended }) => ({
  backgroundColor: 'transparent',
  paddingTop: theme.spacing(4),
  '& .MuiCardHeader-title': {
    fontSize: '1.6rem',
    fontWeight: 600,
    background: isRecommended ? 
      'linear-gradient(90deg, #009688 0%, #4caf50 100%)' : 
      'linear-gradient(90deg, #424242 0%, #757575 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: isRecommended ? 'transparent' : 'inherit',
  },
}));

const PricingChip = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: -14,
  right: 20,
  fontWeight: 'bold',
  fontSize: '0.7rem',
  letterSpacing: '0.05em',
  padding: '0 4px',
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  zIndex: 5,
  boxShadow: '0 4px 8px rgba(0, 150, 136, 0.3)',
  borderRadius: '4px 4px 4px 4px',
  height: 28,
  '&::after': {
    content: '""',
    position: 'absolute',
    width: '100%',
    height: '100%',
    backgroundColor: 'inherit',
    zIndex: -1,
    borderRadius: 'inherit',
  }
}));

const PriceWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'baseline',
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const Price = styled(Typography)(({ theme, isRecommended }) => ({
  fontSize: '3rem',
  fontWeight: 800,
  lineHeight: 1.1,
  color: isRecommended ? theme.palette.primary.dark : theme.palette.text.primary,
  transition: 'color 0.3s ease',
}));

const Period = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  color: theme.palette.text.secondary,
  marginLeft: theme.spacing(0.5),
}));

const Savings = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.secondary.main,
  fontWeight: 600,
  marginBottom: theme.spacing(3),
  position: 'relative',
  display: 'inline-block',
  padding: '4px 12px',
  borderRadius: 12,
  backgroundColor: theme.palette.secondary.light,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: -15,
    width: 10,
    height: 2,
    backgroundColor: theme.palette.secondary.main,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    right: -15,
    width: 10,
    height: 2,
    backgroundColor: theme.palette.secondary.main,
  },
}));

const FeatureList = styled(List)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const SecurePaymentIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
  fontSize: '0.85rem',
  color: theme.palette.text.secondary,
  opacity: 0,
  transform: 'translateY(10px)',
  transition: 'opacity 0.3s ease, transform 0.3s ease',
  '.MuiCard-root:hover &': {
    opacity: 1,
    transform: 'translateY(0)',
  },
}));

const StyledButton = styled(Button)(({ theme, isRecommended }) => ({
  padding: '12px 0',
  fontWeight: 600,
  fontSize: '1rem',
  borderRadius: 12,
  textTransform: 'none',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: isRecommended ? theme.palette.primary.main : 'transparent',
  color: isRecommended ? '#fff' : theme.palette.primary.main,
  border: isRecommended ? 'none' : `2px solid ${theme.palette.primary.main}`,
  boxShadow: isRecommended ? '0 4px 12px rgba(0, 150, 136, 0.3)' : 'none',
  '&:hover': {
    backgroundColor: isRecommended ? theme.palette.primary.dark : theme.palette.primary.light,
    color: isRecommended ? '#fff' : theme.palette.primary.main,
    boxShadow: isRecommended ? '0 6px 16px rgba(0, 150, 136, 0.4)' : 'none',
    transform: 'translateY(-2px)',
  },
  '&:active': {
    transform: 'translateY(1px)',
    boxShadow: isRecommended ? '0 2px 8px rgba(0, 150, 136, 0.3)' : 'none',
  },
}));

const decorativeElementStyles = {
  position: 'absolute',
  width: 60,
  height: 60,
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(0,150,136,0.1) 0%, rgba(255,255,255,0) 70%)',
  zIndex: 0,
};

const PricingPlans = () => {
  const plans = [
    {
      title: 'Monthly',
      price: '$9',
      period: '/mo',
      features: [
        { name: 'Full course content unlocked for all language tracks', included: true },
        { name: 'Add / learn any random courselet as per requirement', included: true },
        { name: 'Blitz mode to power through reviews', included: true },
        { name: 'Unlimited AI conversations', included: true },
        { name: 'Faster AI responses', included: false },
        { name: 'Unlimited attempts to play review game', included: false },
      ],
      buttonText: 'Get Started',
      isRecommended: false,
    },
    {
      title: 'Yearly',
      price: '$60',
      period: '/yr',
      features: [
        { name: 'Full course content unlocked for all language tracks', included: true },
        { name: 'Add / learn any random courselet as per requirement', included: true },
        { name: 'Blitz mode to power through reviews', included: true },
        { name: 'Unlimited AI conversations', included: true },
        { name: 'Faster AI responses', included: true },
        { name: 'Unlimited attempts to play review game', included: true },
      ],
      buttonText: 'Best Value',
      isRecommended: true,
      savings: 'Save $48/year',
    },
    {
      title: 'Lifetime',
      price: '$150',
      period: ' one-time',
      features: [
        { name: 'Full course content unlocked for all language tracks', included: true },
        { name: 'Add / learn any random courselet as per requirement', included: true },
        { name: 'Blitz mode to power through reviews', included: true },
        { name: 'Unlimited AI conversations', included: true },
        { name: 'Faster AI responses', included: true },
        { name: 'Unlimited attempts to play review game', included: true },
      ],
      buttonText: 'Get Lifetime Access',
      isRecommended: false,
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        bgcolor: 'background.default', 
        backgroundImage: 'linear-gradient(180deg, rgba(0,150,136,0.03) 0%, rgba(255,255,255,1) 100%)',
        py: 8,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative elements */}
        <Box sx={{
          ...decorativeElementStyles,
          top: '10%',
          left: '5%',
          width: 120,
          height: 120,
        }} />
        <Box sx={{
          ...decorativeElementStyles,
          bottom: '15%',
          right: '8%',
        }} />
        <Box sx={{
          ...decorativeElementStyles,
          top: '40%',
          right: '15%',
          width: 90,
          height: 90,
        }} />

        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 7, position: 'relative', zIndex: 1 }}>
            <Typography variant="h3" component="h1" gutterBottom sx={{ 
              fontWeight: 700, 
              mb: 1.5,
              background: 'linear-gradient(90deg, #00796b 0%, #388e3c 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block',
            }}>
              Choose Your Plan
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', fontWeight: 400, mb: 2 }}>
              Unlock the full potential of language learning
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center" sx={{ maxWidth: 1200, mx: 'auto' }}>
            {plans.map((plan) => (
              <Grid item xs={12} md={4} key={plan.title} sx={{ position: 'relative' }}>
                <StyledCard isRecommended={plan.isRecommended}>
                  {plan.isRecommended && (
                    <PricingChip 
                      label="RECOMMENDED" 
                      size="small"
                    />
                  )}
                  
                  <StyledCardHeader
                    title={plan.title}
                    titleTypographyProps={{ align: 'center' }}
                    isRecommended={plan.isRecommended}
                  />
                  
                  <CardContent sx={{ flexGrow: 1, px: 3, pb: 4, position: 'relative', zIndex: 1 }}>
                    <PriceWrapper>
                      <Price component="h2" variant="h3" isRecommended={plan.isRecommended}>
                        {plan.price}
                      </Price>
                      <Period variant="h6" color="text.secondary">
                        {plan.period}
                      </Period>
                    </PriceWrapper>

                    {plan.savings && (
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Savings variant="subtitle2">
                          {plan.savings}
                        </Savings>
                      </Box>
                    )}

                    <FeatureList>
                      {plan.features.map((feature) => (
                        <ListItem key={feature.name} sx={{ py: 1, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {feature.included ? (
                              <CheckIcon sx={{ 
                                color: 'success.main',
                                backgroundColor: 'success.light',
                                borderRadius: '50%',
                                padding: '3px',
                                fontSize: '1.2rem',
                              }} />
                            ) : (
                              <CloseIcon sx={{ 
                                color: 'text.disabled',
                                fontSize: '1.2rem',
                              }} />
                            )}
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature.name}
                            primaryTypographyProps={{
                              fontSize: 15,
                              fontWeight: 500,
                              lineHeight: '20px',
                              color: feature.included ? 'text.primary' : 'text.disabled',
                            }}
                          />
                        </ListItem>
                      ))}
                    </FeatureList>

                    <Box sx={{ mt: 'auto' }}>
                      <StyledButton
                        fullWidth
                        variant={plan.isRecommended ? "contained" : "outlined"}
                        color="primary"
                        size="large"
                        isRecommended={plan.isRecommended}
                      >
                        {plan.buttonText}
                      </StyledButton>
                      
                      <SecurePaymentIndicator>
                        <LockIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: theme.palette.primary.main }} />
                        <Typography variant="caption">Secure, encrypted payment</Typography>
                      </SecurePaymentIndicator>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SecurityIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              All payments are handled securely by Stripe.
            </Typography>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default PricingPlans;